// Keyboard shortcuts name Latin characters ("mod+k"), but KeyboardEvent.key
// reports what the active layout produced — on the Russian layout ⌘K arrives
// as "л", so a raw comparison never matches and every shortcut is dead. The
// physical event.code still says KeyK; when the layout typed a letter (or
// digit) of a non-Latin script, the Latin key is recovered from it.
//
// The gate is deliberately narrow: a single character that is a letter or
// digit of a NON-Latin script. Latin letters outside ASCII (German "ö",
// Swedish "å") stay as typed — those layouts can type the bound Latin keys
// directly, and remapping ⌘Ö to the physical ";" would fire bindings the user
// never meant. Symbols are excluded for the same reason: macOS Option and
// AltGr produce things like "€" or "˚" on plain Latin layouts, and a fallback
// there would swallow the character whenever its physical position carries a
// binding.

// Punctuation positions that carry letters on non-Latin layouts (on Russian:
// ё on Backquote, х/ъ on the brackets, ж on Semicolon, э on Quote, б/ю on
// Comma/Period). Values are the characters the same positions type on the
// US layout — what shortcut configs are written in.
const PUNCTUATION_CODES: Record<string, string> = {
  Comma: ",",
  Period: ".",
  Semicolon: ";",
  Quote: "'",
  BracketLeft: "[",
  BracketRight: "]",
  Backquote: "`",
  Slash: "/",
  Backslash: "\\",
  Minus: "-",
  Equal: "=",
}

const NON_LATIN_LETTER_OR_DIGIT = /^(?![\p{Script=Latin}])[\p{L}\p{Nd}]$/u

function latinKeyFromCode(code: string) {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase()
  if (/^Digit[0-9]$/.test(code)) return code.slice(5)
  return PUNCTUATION_CODES[code]
}

type LayoutKeyEvent = Pick<KeyboardEvent, "key" | "code">

// The Latin key at the event's physical position, or undefined when the event
// should be matched by what it typed (ASCII, Latin-script letters, symbols,
// named keys like "Enter").
export function layoutFallbackKey(event: LayoutKeyEvent): string | undefined {
  const char = event.key
  if (char.length !== 1 || char.charCodeAt(0) < 0x80) return
  if (!NON_LATIN_LETTER_OR_DIGIT.test(char)) return
  return latinKeyFromCode(event.code)
}

// Drop-in replacement for `event.key.toLowerCase()` in shortcut handlers.
export function layoutAwareKey(event: LayoutKeyEvent): string {
  return layoutFallbackKey(event) ?? event.key.toLowerCase()
}
